import { z } from "zod";
import {
  createRoute,
  redirect,
  retainSearchParams,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { SupplierListScreen } from "@/app/Suppliers/Screens/SupplierListScreen";
import { SupplierCreateScreen } from "@/app/Suppliers/Screens/SupplierCreateScreen";
import { SupplierRecordScreen } from "@/app/Suppliers/Screens/SupplierRecordScreen";
import { SupplierRelocationScreen } from "@/app/Suppliers/Screens/SupplierRelocationScreen";
import { SupplierBookingScreen } from "@/app/Suppliers/Screens/SupplierBookingScreen";
import { SupplierAdsScreen } from "@/app/Suppliers/Screens/SupplierAdsScreen";
import { getSupplierListQueryVariables } from "@/app/Suppliers/Utils/getSupplierListQueryVariables";
import { appLayoutRoute } from "@/routes/layout-routes";
import {
  relocationTableSearchKeys,
  relocationTableSearchParams,
} from "@/app/Relocations/Utils/relocationTableSearchParams";
import {
  fetchQuery,
  prefetchInfiniteQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { supplierListQuery } from "@/app/Suppliers/GraphQL/supplierListQuery";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { SupplierTableScreen } from "@/app/Suppliers/Screens/SupplierTableScreen";

const supplierSearchParams = z.object({});
export type SupplierListSearchParams = z.infer<typeof supplierSearchParams>;

export const suppliersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/suppliers",
  loader: ({ params }) => {
    const variables = getSupplierListQueryVariables({});
    return prefetchInfiniteQuery(supplierListQuery, variables, params.orgId);
  },
  head: () => ({
    meta: [
      {
        title: "Suppliers - Imoova Admin",
      },
    ],
  }),
});

export const supplierTableRoute = createRoute({
  getParentRoute: () => suppliersRoute,
  path: "/",
  component: SupplierTableScreen,
  head: () => ({
    meta: [
      {
        title: "Suppliers - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage suppliers",
      },
    ],
  }),
});

function redirectToSupplierSettings(
  to:
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings"
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings/insurance"
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings/billing"
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings/vehicles"
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings/offices"
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings/active-lines"
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings/activity"
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings/notifications"
    | "/$tenant/$orgId/supplier/{-$supplierId}/settings/reports",
) {
  return ({
    params: { id, ...params },
  }: {
    params: { id: string; tenant: string; orgId: string; supplierId?: string };
  }) => {
    throw redirect({
      to,
      params,
      search: { supplier: id },
      replace: true,
    });
  };
}

export const supplierRecordLayout = createRoute({
  getParentRoute: () => suppliersRoute,
  id: "_supplierRecordLayout",
  component: SupplierListScreen,
  validateSearch: zodValidator(supplierSearchParams),
  loader: ({ params }) => {
    const variables = getSupplierListQueryVariables({});
    return prefetchInfiniteQuery(supplierListQuery, variables, params.orgId);
  },
});

export const supplierRecordRoute = createRoute({
  getParentRoute: () => supplierRecordLayout,
  path: "$id",
  component: SupplierRecordScreen,
  loader: ({ params }) => {
    return fetchQuery(
      supplierRecordQuery,
      {
        id: params.id,
      },
      params.orgId,
    );
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.supplier.name ?? "Supplier"} - Imoova Admin`,
      },
    ],
  }),
});

export const supplierCreateRoute = createRoute({
  getParentRoute: () => suppliersRoute,
  path: "/create",
  component: SupplierCreateScreen,
  head: () => ({
    meta: [
      {
        title: "Create Supplier - Imoova Admin",
      },
    ],
  }),
});

export const supplierDetailRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers/$id/bookings",
      params,
      replace: true,
    });
  },
});

export const supplierInsuranceRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/insurance",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/insurance",
  ),
});

export const supplierBillingRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/billing",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/billing",
  ),
});

export const supplierVehiclesRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/vehicles",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/vehicles",
  ),
});

export const supplierOfficesRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/offices",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/offices",
  ),
});

export const supplierLogisticsRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/logistics",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/offices",
  ),
});

const supplierRelocationSearchParams = relocationTableSearchParams;
export const supplierRelocationsRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/relocations",
  component: SupplierRelocationScreen,
  validateSearch: zodValidator(supplierRelocationSearchParams),
  search: {
    middlewares: [retainSearchParams(relocationTableSearchKeys)],
  },
});

export const supplierGapRentalsRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/gap-rentals",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers/$id/relocations",
      params,
      replace: true,
    });
  },
});

export const supplierBookingsRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/bookings",
  component: SupplierBookingScreen,
});

export const supplierAdsRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/ads",
  component: SupplierAdsScreen,
});

export const supplierActiveLinesRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/active-lines",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/active-lines",
  ),
});

export const supplierDefaultHoursRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/default-hours",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/offices",
  ),
});

export const supplierActivityRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/activity",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/activity",
  ),
});

export const supplierNotificationRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/notifications",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/notifications",
  ),
});

export const supplierReportsRoute = createRoute({
  getParentRoute: () => supplierRecordRoute,
  path: "/reports",
  beforeLoad: redirectToSupplierSettings(
    "/$tenant/$orgId/supplier/{-$supplierId}/settings/reports",
  ),
});
